import React, { useState, useRef, useEffect } from "react";
import { Send, Trash2 } from "lucide-react";

/**
 * ChatInput - Input field with send button and clear chat option
 * @param {Function} onSend - Callback when message is sent
 * @param {boolean} disabled - Whether input is disabled
 * @param {Function} onClear - Callback to clear chat
 * @param {boolean} hasMessages - Whether there are messages to clear
 * @returns {JSX.Element}
 */
const ChatInput = ({ onSend, disabled, onClear, hasMessages }) => {
    const [input, setInput] = useState("");
    const textareaRef = useRef(null);

    // Auto-resize textarea
    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = "auto";
            textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
        }
    }, [input]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (input.trim() && !disabled) {
            onSend(input);
            setInput("");
            // Reset height after clearing
            if (textareaRef.current) {
                textareaRef.current.style.height = "auto";
            }
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    return (
        <div className="border-t border-gray-200 bg-white p-3">
            <form onSubmit={handleSubmit} className="flex gap-3 items-end">
                <div className="flex-1 relative">
                    <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask about shipments, invoices, or orders..."
                        disabled={disabled}
                        rows={1}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl resize-none text-fluid-base focus:outline-none focus:ring-2 focus:ring-acs-primary-green focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all min-h-[48px] max-h-[120px]"
                    />
                </div>
                <button
                    type="submit"
                    disabled={disabled || !input.trim()}
                    className="p-3 bg-acs-primary-green text-black rounded-2xl hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md flex-shrink-0"
                    aria-label="Send message"
                >
                    <Send className="w-5 h-5" />
                </button>
                {hasMessages && (
                    <button
                        type="button"
                        onClick={onClear}
                        disabled={disabled}
                        className="p-3 bg-gray-100 text-gray-600 rounded-2xl hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex-shrink-0"
                        aria-label="Clear chat"
                        title="Clear chat"
                    >
                        <Trash2 className="w-5 h-5" />
                    </button>
                )}
            </form>
            <p className="text-[10px] text-gray-400 mt-1.5 px-2">
                Press Enter to send, Shift + Enter for new line
            </p>
        </div>
    );
};

export default ChatInput;
